/**
 * Test Runner Dashboard — Admin page for super_admin/ops_admin
 *
 * Allows running sandbox, smoke, API contract, data isolation,
 * and API access tests directly from the browser with live results.
 * Includes pytest suite execution, coverage reports, and batch result logs.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  PlayCircle, RefreshCw, CheckCircle, XCircle, AlertTriangle,
  Clock, Loader2, ChevronDown, ChevronRight, History, Zap,
  Shield, Database, Globe, Lock, FlaskConical, BarChart3,
  Package, Users, MessageSquare, FileText, CreditCard,
  Briefcase, Activity, Download, ArrowRight, FormInput, Sliders,
  Sparkles, Plus, Trash2, Link2, Copy, Lightbulb, ListTree, FileCode, Wand2
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { authFetch } from '@/lib/authFetch';

interface TestCheck {
  name: string;
  status: 'pass' | 'fail' | 'error';
  detail: string;
  duration_ms: number;
}

interface TestResult {
  category: string;
  status: 'pass' | 'fail' | 'error';
  checks: TestCheck[];
  total: number;
  passed: number;
  failed: number;
  duration_ms: number;
}

interface TestCategory {
  id: string;
  name: string;
  description: string;
  requires_db: boolean;
  requires_cbo_id: boolean;
  last_run: {
    id: number;
    status: string;
    total: number;
    passed: number;
    failed: number;
    duration_ms: number;
    timestamp: string;
  } | null;
}

interface HistoryRun {
  id: number;
  category: string;
  status: string;
  total: number;
  passed: number;
  failed: number;
  duration_ms: number;
  cbo_id: number;
  triggered_by_name: string;
  timestamp: string;
  source?: string;
  repo?: string;
  commit_sha?: string;
  branch?: string;
}

// Auto-pipeline summary: one row per (repo, source) — backend + frontend
// for stop_hook | pre_push | nightly | deploy_gate. Rendered as a 4-card
// grid in the Custom Regressions tab so a single glance shows whether each
// automated guard rail is currently green.
interface AutoPipelineEntry {
  repo: 'backend' | 'frontend';
  source: 'stop_hook' | 'pre_push' | 'nightly' | 'deploy_gate';
  last_run: {
    id: number;
    category: string;
    status: 'pass' | 'fail' | 'error';
    total: number;
    passed: number;
    failed: number;
    duration_ms: number;
    commit_sha?: string;
    branch?: string;
    timestamp: string;
  } | null;
}

const PIPELINE_LABELS: Record<string, string> = {
  stop_hook: 'Stop Hook (per-session)',
  pre_push: 'Pre-Push',
  nightly: 'Nightly (GitHub Actions)',
  deploy_gate: 'Deploy Gate (Render build)',
};

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  'sandbox': <Database className="w-5 h-5" />,
  'smoke': <Zap className="w-5 h-5" />,
  'api-contracts': <Globe className="w-5 h-5" />,
  'data-isolation': <Shield className="w-5 h-5" />,
  'api-access': <Lock className="w-5 h-5" />,
};

const STATUS_STYLES: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
  pass: { bg: 'bg-green-50 border-green-200', text: 'text-green-700', icon: <CheckCircle className="w-4 h-4 text-green-600" /> },
  fail: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', icon: <XCircle className="w-4 h-4 text-red-600" /> },
  error: { bg: 'bg-yellow-50 border-yellow-200', text: 'text-yellow-700', icon: <AlertTriangle className="w-4 h-4 text-yellow-600" /> },
  running: { bg: 'bg-blue-50 border-blue-200', text: 'text-blue-700', icon: <Loader2 className="w-4 h-4 text-blue-600 animate-spin" /> },
};

interface OrgOption { id: number; name: string; is_sandbox?: boolean; }

// -- Pytest suite types --
interface PytestSuite {
  id: string;
  name: string;
  description: string;
  path: string;
  safe_for_production: boolean;
  requires_db: boolean;
}

interface PytestSuiteGroup {
  id: string;
  name: string;
  description: string;
  suites: string[];
}

interface PytestSuiteResult {
  suite: string;
  suite_name: string;
  status: 'pass' | 'fail' | 'error';
  total: number;
  passed: number;
  failed: number;
  skipped: number;
  pass_rate: string;
  duration_ms: number;
  batches?: Record<string, { total: number; passed: number; failed: number; skipped: number; pass_rate: string }>;
  checks?: TestCheck[];
  all_failures?: { nodeid: string; longrepr: string }[];
  output?: string;
  error?: string;
}

interface CoverageReport {
  available: boolean;
  message?: string;
  session_id?: string;
  environment?: string;
  timestamp?: string;
  elapsed_s?: number;
  total?: number;
  passed?: number;
  failed?: number;
  skipped?: number;
  pass_rate?: string;
  batches?: Record<string, { total: number; passed: number; failed: number; skipped: number; pass_rate: string }>;
  all_failures?: { nodeid: string; longrepr: string }[];
}

// -- Suite scope (the actual tests a suite covers, parsed without running) --
interface SuiteScopeTest {
  nodeid: string;
  name: string;
  classname: string | null;
  file: string;
  line: number;
  docstring: string;
  markers: string[];
}
interface SuiteScope {
  suite_id: string;
  name: string;
  description: string;
  path: string;
  marker_filter: string | null;
  count: number;
  tests: SuiteScopeTest[];
}

// -- Documented scope notes (user/AI gap backlog) --
interface ScopeNote {
  id: number;
  suite_id: string;
  repo: string;
  surface: string;
  title: string;
  description: string;
  status: 'proposed' | 'codified' | 'wont_do';
  origin: 'user' | 'ai';
  ai_rationale: string | null;
  linked_nodeid: string | null;
  draft_code: string | null;
  draft_lang: string | null;
  draft_target_path: string | null;
  draft_generated_at: string | null;
  author_user_id: number | null;
  cbo_id: number | null;
  created_at: string | null;
  updated_at: string | null;
}

const NOTE_STATUS_STYLES: Record<string, string> = {
  proposed: 'bg-amber-100 text-amber-700',
  codified: 'bg-green-100 text-green-700',
  wont_do: 'bg-gray-100 text-gray-500',
};

// -- Dev report: aggregated scope notes across all suites --
interface ScopeReportSuite {
  suite_id: string;
  surface: string;
  repo: string;
  name: string;
  counts: Record<string, number>;
  notes: ScopeNote[];
}
interface ScopeReport {
  totals: Record<string, number>;
  suites: ScopeReportSuite[];
}

// Stable key for a scope panel — surface namespaces the suite/category id so a
// pytest suite and an API category with the same id can't collide.
const scopeKey = (surface: string, id: string) => `${surface}:${id}`;

/**
 * Read-only list of the actual tests/checks a suite covers — lets users review
 * scope before running. `tests` come from either get_suite_scope (pytest) or
 * the API check definitions, normalized to {label, detail, meta}.
 */
function ScopeTestList({
  items, loading, markerFilter, emptyHint,
}: {
  items: { label: string; detail?: string; meta?: string; markers?: string[] }[];
  loading: boolean;
  markerFilter?: string | null;
  emptyHint: string;
}) {
  if (loading) {
    return (
      <div className="px-4 py-4 text-sm text-gray-400 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading scope…
      </div>
    );
  }
  if (!items.length) {
    return <div className="px-4 py-4 text-sm text-gray-400">{emptyHint}</div>;
  }
  return (
    <div className="px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <ListTree className="w-4 h-4 text-gray-500" />
        <h4 className="text-xs font-medium text-gray-500 uppercase">Scope — {items.length} test{items.length === 1 ? '' : 's'} this covers</h4>
        {markerFilter && (
          <span className="text-xs text-gray-400">
            (runs only tests marked <code className="bg-gray-100 px-1 rounded">{markerFilter}</code>)
          </span>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto border border-gray-200 rounded bg-white divide-y divide-gray-100">
        {items.map((it, i) => (
          <div key={i} className="px-3 py-1.5 text-xs">
            <div className="flex items-center gap-2">
              <span className="font-mono text-gray-800">{it.label}</span>
              {(it.markers || []).map(m => (
                <span key={m} className="px-1 py-0.5 rounded bg-indigo-50 text-indigo-600 text-[10px]">{m}</span>
              ))}
              {it.meta && <span className="text-gray-400">{it.meta}</span>}
            </div>
            {it.detail && <div className="text-gray-500 mt-0.5">{it.detail}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * "Documented test scope / gaps" panel — lists scope notes, lets users add an
 * idea, run AI gap-suggestion, generate AI draft code, and walk a note from
 * proposed → codified (linking the real test nodeid). Shown under both the
 * Pytest and API-check expand blocks.
 */
function ScopeNotesPanel({
  suiteId, repo, surface, notes, loading, scopeNodeids,
  onAdd, onUpdate, onDelete, onSuggest, onDraft, suggesting, draftingIds,
}: {
  suiteId: string; repo: string; surface: string;
  notes: ScopeNote[]; loading: boolean; scopeNodeids: string[];
  onAdd: (title: string, description: string) => void;
  onUpdate: (id: number, patch: Partial<ScopeNote>) => void;
  onDelete: (id: number) => void;
  onSuggest: () => void;
  onDraft: (id: number) => void;
  suggesting: boolean;
  draftingIds: Record<number, boolean>;
}) {
  const [showAdd, setShowAdd] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [openDraft, setOpenDraft] = useState<Record<number, boolean>>({});
  const coveredSet = new Set(scopeNodeids);

  const submit = () => {
    if (!title.trim() || !description.trim()) return;
    onAdd(title.trim(), description.trim());
    setTitle(''); setDescription(''); setShowAdd(false);
  };

  return (
    <div className="border-t border-gray-200 bg-white px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <h4 className="text-xs font-medium text-gray-500 uppercase">Documented test scope / gaps</h4>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onSuggest}
            disabled={suggesting}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50"
            title="Ask AI to propose missing tests for this suite"
          >
            {suggesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
            Suggest missing tests (AI)
          </button>
          <button
            onClick={() => setShowAdd(s => !s)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
          >
            <Plus className="w-3 h-3" /> Add test idea
          </button>
        </div>
      </div>

      {showAdd && (
        <div className="mb-3 p-3 border border-gray-200 rounded bg-gray-50 space-y-2">
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Title — e.g. Reject login when account is locked"
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          />
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="What should the test set up, do, and assert?"
            rows={3}
            className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setShowAdd(false)} className="px-2 py-1 text-xs text-gray-500">Cancel</button>
            <button onClick={submit} className="px-3 py-1 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700">Save</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading notes…</div>
      ) : notes.length === 0 ? (
        <div className="text-sm text-gray-400">No documented gaps yet. Add a test idea or let AI suggest some.</div>
      ) : (
        <div className="space-y-2">
          {notes.map(note => {
            const isCovered = !!note.linked_nodeid && coveredSet.has(note.linked_nodeid);
            const drafting = draftingIds[note.id];
            return (
              <div key={note.id} className="border border-gray-200 rounded p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900">{note.title}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${NOTE_STATUS_STYLES[note.status]}`}>{note.status}</span>
                      {note.origin === 'ai' && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-600 flex items-center gap-0.5"><Sparkles className="w-2.5 h-2.5" /> AI</span>
                      )}
                      {isCovered && (
                        <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700">✓ covered — in runner & nightly</span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mt-1 whitespace-pre-wrap">{note.description}</p>
                    {note.ai_rationale && (
                      <p className="text-xs text-purple-600 mt-1"><em>Why: {note.ai_rationale}</em></p>
                    )}
                    {note.linked_nodeid && (
                      <p className="text-xs text-gray-400 mt-1 font-mono flex items-center gap-1"><Link2 className="w-3 h-3" />{note.linked_nodeid}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => onDraft(note.id)}
                      disabled={drafting}
                      title="Generate a candidate test (AI) — review & commit it yourself"
                      className="flex items-center gap-1 px-1.5 py-1 text-[11px] bg-purple-50 text-purple-700 rounded hover:bg-purple-100 disabled:opacity-50"
                    >
                      {drafting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
                      Draft test
                    </button>
                    <button onClick={() => onDelete(note.id)} title="Delete" className="p-1 text-gray-400 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>

                {/* Status / link controls */}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  <select
                    value={note.status}
                    onChange={e => onUpdate(note.id, { status: e.target.value as ScopeNote['status'] })}
                    className="text-xs border border-gray-300 rounded px-1 py-0.5"
                  >
                    <option value="proposed">proposed</option>
                    <option value="codified">codified</option>
                    <option value="wont_do">wont_do</option>
                  </select>
                  {scopeNodeids.length > 0 && (
                    <select
                      value={note.linked_nodeid || ''}
                      onChange={e => onUpdate(note.id, { linked_nodeid: e.target.value || null } as Partial<ScopeNote>)}
                      className="text-xs border border-gray-300 rounded px-1 py-0.5 max-w-xs"
                      title="Link the real test that now covers this note"
                    >
                      <option value="">— link covering test —</option>
                      {scopeNodeids.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  )}
                </div>

                {/* AI draft code */}
                {note.draft_code && (
                  <div className="mt-2">
                    <button
                      onClick={() => setOpenDraft(p => ({ ...p, [note.id]: !p[note.id] }))}
                      className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                    >
                      {openDraft[note.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      <FileCode className="w-3 h-3" /> AI draft test
                      {note.draft_target_path && <span className="text-gray-400 font-mono">→ {note.draft_target_path}</span>}
                    </button>
                    {openDraft[note.id] && (
                      <div className="mt-1 relative">
                        <button
                          onClick={() => navigator.clipboard?.writeText(note.draft_code || '')}
                          className="absolute right-2 top-2 flex items-center gap-1 px-1.5 py-0.5 text-[10px] bg-gray-700 text-white rounded hover:bg-gray-600"
                        >
                          <Copy className="w-2.5 h-2.5" /> Copy
                        </button>
                        <pre className="text-xs bg-gray-900 text-gray-100 rounded p-3 max-h-72 overflow-auto whitespace-pre-wrap">{note.draft_code}</pre>
                        <p className="text-[11px] text-amber-600 mt-1">Review and commit this into the repo yourself — it isn't written automatically. Once committed under the suite path, it runs in the runner & nightly.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SUITE_ICONS: Record<string, React.ReactNode> = {
  smoke: <Zap className="w-4 h-4" />,
  contracts: <Globe className="w-4 h-4" />,
  security: <Shield className="w-4 h-4" />,
  case_mgmt: <FileText className="w-4 h-4" />,
  case_mgmt_basic: <FileText className="w-4 h-4" />,
  comms: <MessageSquare className="w-4 h-4" />,
  comms_basic: <MessageSquare className="w-4 h-4" />,
  contacts: <Users className="w-4 h-4" />,
  service_referrals: <ArrowRight className="w-4 h-4" />,
  members: <Users className="w-4 h-4" />,
  auth: <Lock className="w-4 h-4" />,
  billing: <CreditCard className="w-4 h-4" />,
  programs: <Package className="w-4 h-4" />,
  workforce: <Briefcase className="w-4 h-4" />,
  documents: <FileText className="w-4 h-4" />,
  integration: <Activity className="w-4 h-4" />,
  eligibility: <Shield className="w-4 h-4" />,
  unit: <FlaskConical className="w-4 h-4" />,
};

export default function TestRunnerDashboard() {
  const { t } = useAppTranslation();
  const apiUrl = getWeaveinApiUrl();
  const [categories, setCategories] = useState<TestCategory[]>([]);
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [running, setRunning] = useState<Record<string, boolean>>({});
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [checkDefs, setCheckDefs] = useState<Record<string, any[]>>({});
  const [history, setHistory] = useState<HistoryRun[]>([]);
  const [activeTab, setActiveTab] = useState<'tests' | 'pytest' | 'scope-notes' | 'history' | 'regressions'>('tests');
  const [regressionResult, setRegressionResult] = useState<TestResult | null>(null);
  const [regressionRunning, setRegressionRunning] = useState(false);
  const [formFieldSyncResult, setFormFieldSyncResult] = useState<TestResult | null>(null);
  const [formFieldSyncRunning, setFormFieldSyncRunning] = useState(false);
  const [fieldConfigResult, setFieldConfigResult] = useState<TestResult | null>(null);
  const [fieldConfigRunning, setFieldConfigRunning] = useState(false);
  const [phoneChange2faResult, setPhoneChange2faResult] = useState<TestResult | null>(null);
  const [phoneChange2faRunning, setPhoneChange2faRunning] = useState(false);
  const [autoPipelines, setAutoPipelines] = useState<AutoPipelineEntry[]>([]);
  const [autoPipelinesLoading, setAutoPipelinesLoading] = useState(false);
  const [historySourceFilter, setHistorySourceFilter] = useState<string>('');
  // Per-row drilldown: when a row is expanded, fetch /history/{id} and cache
  // the detail payload (checks + failures) so subsequent expand/collapse is
  // instant. `detailLoading` tracks in-flight fetches.
  const [historyExpanded, setHistoryExpanded] = useState<Record<number, boolean>>({});
  const [historyDetail, setHistoryDetail] = useState<Record<number, any>>({});
  const [historyDetailLoading, setHistoryDetailLoading] = useState<Record<number, boolean>>({});
  const [loading, setLoading] = useState(true);

  // -- Pytest suite state --
  const [pytestSuites, setPytestSuites] = useState<PytestSuite[]>([]);
  const [pytestGroups, setPytestGroups] = useState<PytestSuiteGroup[]>([]);
  const [pytestResults, setPytestResults] = useState<Record<string, PytestSuiteResult>>({});
  const [pytestRunning, setPytestRunning] = useState<Record<string, boolean>>({});
  const [pytestExpanded, setPytestExpanded] = useState<Record<string, boolean>>({});
  const [coverageReport, setCoverageReport] = useState<CoverageReport | null>(null);
  const [coverageLoading, setCoverageLoading] = useState(false);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [targetCboId, setTargetCboId] = useState<string>(localStorage.getItem('active_cbo_id') || '');

  // -- Suite scope + documented scope-notes state --
  const [suiteScope, setSuiteScope] = useState<Record<string, SuiteScope>>({});
  const [suiteScopeLoading, setSuiteScopeLoading] = useState<Record<string, boolean>>({});
  const [scopeNotes, setScopeNotes] = useState<Record<string, ScopeNote[]>>({});
  const [scopeNotesLoading, setScopeNotesLoading] = useState<Record<string, boolean>>({});
  const [scopeSuggesting, setScopeSuggesting] = useState<Record<string, boolean>>({});
  const [scopeDrafting, setScopeDrafting] = useState<Record<number, boolean>>({});
  const [scopeReport, setScopeReport] = useState<ScopeReport | null>(null);
  const [scopeReportLoading, setScopeReportLoading] = useState(false);

  const getHeaders = useCallback(() => ({
    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
    'X-CBO-ID': targetCboId,
    'Content-Type': 'application/json',
  }), [targetCboId]);

  // Fetch organizations for the selector
  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const r = await authFetch(`${apiUrl}/api/cbos/users/me/organizations`);
        if (r.ok) {
          const data = await r.json();
          const orgList = (Array.isArray(data) ? data : data.organizations || []).map((o: any) => ({
            id: o.cbo_id || o.id,
            name: o.organization?.name || o.name || `Org ${o.cbo_id || o.id}`,
            is_sandbox: o.organization?.is_test_data || o.is_test_data || false,
          }));
          setOrgs(orgList);
          // Auto-select sandbox org if one exists and current target isn't a sandbox
          const sandbox = orgList.find((o: OrgOption) => o.is_sandbox);
          if (sandbox && !orgList.find((o: OrgOption) => o.id === parseInt(targetCboId))) {
            setTargetCboId(String(sandbox.id));
          }
        }
      } catch { /* non-critical */ }
    };
    fetchOrgs();
  }, [apiUrl]);

  const fetchCategories = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/categories`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        const cats = data.categories || [];
        setCategories(cats);
        // Preload check definitions for frontend categories
        for (const cat of cats) {
          if (cat.execution === 'frontend') {
            try {
              const cr = await fetch(`${apiUrl}/api/admin/tests/${cat.id}/checks`, { headers: getHeaders() });
              if (cr.ok) {
                const cd = await cr.json();
                setCheckDefs(prev => ({ ...prev, [cat.id]: cd.checks || [] }));
              }
            } catch { /* non-critical */ }
          }
        }
      }
    } catch (err) {
      console.error('Failed to fetch test categories:', err);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, getHeaders]);

  const fetchHistory = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ limit: '50' });
      if (historySourceFilter) qs.set('source', historySourceFilter);
      const r = await fetch(`${apiUrl}/api/admin/tests/history?${qs}`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setHistory(data.runs || []);
      }
    } catch (err) {
      console.error('Failed to fetch test history:', err);
    }
  }, [apiUrl, getHeaders, historySourceFilter]);

  const toggleHistoryRow = useCallback(async (runId: number) => {
    const wasExpanded = historyExpanded[runId];
    setHistoryExpanded(prev => ({ ...prev, [runId]: !wasExpanded }));
    if (wasExpanded || historyDetail[runId]) return;
    setHistoryDetailLoading(prev => ({ ...prev, [runId]: true }));
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/history/${runId}`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setHistoryDetail(prev => ({ ...prev, [runId]: data }));
      }
    } catch (err) {
      console.error('Failed to fetch run detail:', err);
    } finally {
      setHistoryDetailLoading(prev => ({ ...prev, [runId]: false }));
    }
  }, [apiUrl, getHeaders, historyExpanded, historyDetail]);

  const fetchAutoPipelines = useCallback(async () => {
    setAutoPipelinesLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/auto-pipeline`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setAutoPipelines(data.pipelines || []);
      }
    } catch (err) {
      console.error('Failed to fetch auto-pipeline status:', err);
    } finally {
      setAutoPipelinesLoading(false);
    }
  }, [apiUrl, getHeaders]);

  // -- Pytest suite fetchers --
  const fetchPytestSuites = useCallback(async () => {
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/pytest-suites`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setPytestSuites(data.suites || []);
        setPytestGroups(data.groups || []);
      }
    } catch (err) {
      console.error('Failed to fetch pytest suites:', err);
    }
  }, [apiUrl, getHeaders]);

  const fetchCoverage = useCallback(async () => {
    setCoverageLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/coverage`, { headers: getHeaders() });
      if (r.ok) {
        setCoverageReport(await r.json());
      }
    } catch (err) {
      console.error('Failed to fetch coverage:', err);
    } finally {
      setCoverageLoading(false);
    }
  }, [apiUrl, getHeaders]);

  const runPytestSuite = async (suiteId: string) => {
    setPytestRunning(prev => ({ ...prev, [suiteId]: true }));
    setPytestExpanded(prev => ({ ...prev, [suiteId]: true }));
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/pytest-suites/${suiteId}/run`, {
        method: 'POST', headers: getHeaders(),
      });
      if (r.ok) {
        const result: PytestSuiteResult = await r.json();
        setPytestResults(prev => ({ ...prev, [suiteId]: result }));
      }
    } catch (err) {
      console.error(`Failed to run pytest suite ${suiteId}:`, err);
      setPytestResults(prev => ({
        ...prev,
        [suiteId]: {
          suite: suiteId, suite_name: suiteId, status: 'error',
          total: 0, passed: 0, failed: 0, skipped: 0, pass_rate: 'N/A',
          duration_ms: 0, error: String(err),
        },
      }));
    } finally {
      setPytestRunning(prev => ({ ...prev, [suiteId]: false }));
      fetchHistory();
    }
  };

  const runPytestGroup = async (groupId: string) => {
    const group = pytestGroups.find(g => g.id === groupId);
    if (!group) return;
    for (const suiteId of group.suites) {
      await runPytestSuite(suiteId);
    }
  };

  // -- Suite scope + scope-notes helpers --
  const fetchSuiteScope = useCallback(async (suiteId: string) => {
    if (suiteScope[suiteId] || suiteScopeLoading[suiteId]) return;
    setSuiteScopeLoading(prev => ({ ...prev, [suiteId]: true }));
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/pytest-suites/${suiteId}/tests`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setSuiteScope(prev => ({ ...prev, [suiteId]: data }));
      }
    } catch (err) {
      console.error(`Failed to fetch scope for ${suiteId}:`, err);
    } finally {
      setSuiteScopeLoading(prev => ({ ...prev, [suiteId]: false }));
    }
  }, [apiUrl, getHeaders, suiteScope, suiteScopeLoading]);

  const fetchScopeNotes = useCallback(async (suiteId: string, repo: string, surface: string) => {
    const key = scopeKey(surface, suiteId);
    setScopeNotesLoading(prev => ({ ...prev, [key]: true }));
    try {
      const qs = new URLSearchParams({ suite_id: suiteId, repo, surface });
      const r = await fetch(`${apiUrl}/api/admin/tests/scope-notes?${qs}`, { headers: getHeaders() });
      if (r.ok) {
        const data = await r.json();
        setScopeNotes(prev => ({ ...prev, [key]: data.notes || [] }));
      }
    } catch (err) {
      console.error('Failed to fetch scope notes:', err);
    } finally {
      setScopeNotesLoading(prev => ({ ...prev, [key]: false }));
    }
  }, [apiUrl, getHeaders]);

  const createScopeNote = useCallback(async (suiteId: string, repo: string, surface: string, title: string, description: string) => {
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/scope-notes`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ suite_id: suiteId, repo, surface, title, description }),
      });
      if (r.ok) await fetchScopeNotes(suiteId, repo, surface);
    } catch (err) {
      console.error('Failed to create scope note:', err);
    }
  }, [apiUrl, getHeaders, fetchScopeNotes]);

  const updateScopeNote = useCallback(async (id: number, patch: Partial<ScopeNote>, suiteId: string, repo: string, surface: string) => {
    const key = scopeKey(surface, suiteId);
    // Optimistic update so the select reflects immediately
    setScopeNotes(prev => ({ ...prev, [key]: (prev[key] || []).map(n => n.id === id ? { ...n, ...patch } : n) }));
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/scope-notes/${id}`, {
        method: 'PATCH', headers: getHeaders(), body: JSON.stringify(patch),
      });
      if (r.ok) {
        const updated = await r.json();
        setScopeNotes(prev => ({ ...prev, [key]: (prev[key] || []).map(n => n.id === id ? updated : n) }));
      }
    } catch (err) {
      console.error('Failed to update scope note:', err);
    }
  }, [apiUrl, getHeaders]);

  const deleteScopeNote = useCallback(async (id: number, suiteId: string, repo: string, surface: string) => {
    const key = scopeKey(surface, suiteId);
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/scope-notes/${id}`, { method: 'DELETE', headers: getHeaders() });
      if (r.ok) setScopeNotes(prev => ({ ...prev, [key]: (prev[key] || []).filter(n => n.id !== id) }));
    } catch (err) {
      console.error('Failed to delete scope note:', err);
    }
  }, [apiUrl, getHeaders]);

  const suggestScopeGaps = useCallback(async (suiteId: string, repo: string, surface: string) => {
    const key = scopeKey(surface, suiteId);
    setScopeSuggesting(prev => ({ ...prev, [key]: true }));
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/scope-notes/suggest`, {
        method: 'POST', headers: getHeaders(),
        body: JSON.stringify({ suite_id: suiteId, repo, surface }),
      });
      if (r.ok) {
        await fetchScopeNotes(suiteId, repo, surface);
      } else {
        const err = await r.json().catch(() => ({}));
        alert(`AI suggestion failed: ${err.detail || r.status}`);
      }
    } catch (err) {
      console.error('Failed to suggest scope gaps:', err);
    } finally {
      setScopeSuggesting(prev => ({ ...prev, [key]: false }));
    }
  }, [apiUrl, getHeaders, fetchScopeNotes]);

  const draftScopeNoteCode = useCallback(async (id: number, suiteId: string, repo: string, surface: string) => {
    const key = scopeKey(surface, suiteId);
    setScopeDrafting(prev => ({ ...prev, [id]: true }));
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/scope-notes/${id}/draft-code`, { method: 'POST', headers: getHeaders() });
      if (r.ok) {
        const updated = await r.json();
        setScopeNotes(prev => ({ ...prev, [key]: (prev[key] || []).map(n => n.id === id ? { ...n, ...updated } : n) }));
      } else {
        const err = await r.json().catch(() => ({}));
        alert(`AI draft failed: ${err.detail || r.status}`);
      }
    } catch (err) {
      console.error('Failed to draft test code:', err);
    } finally {
      setScopeDrafting(prev => ({ ...prev, [id]: false }));
    }
  }, [apiUrl, getHeaders]);

  const fetchScopeReport = useCallback(async () => {
    setScopeReportLoading(true);
    try {
      const r = await fetch(`${apiUrl}/api/admin/tests/scope-notes/report`, { headers: getHeaders() });
      if (r.ok) setScopeReport(await r.json());
    } catch (err) {
      console.error('Failed to fetch scope report:', err);
    } finally {
      setScopeReportLoading(false);
    }
  }, [apiUrl, getHeaders]);

  // Lazy-load scope + notes when a panel is opened.
  const openPytestScope = useCallback((suiteId: string) => {
    fetchSuiteScope(suiteId);
    fetchScopeNotes(suiteId, 'backend', 'pytest');
  }, [fetchSuiteScope, fetchScopeNotes]);
  const openApiCheckScope = useCallback((categoryId: string) => {
    fetchScopeNotes(categoryId, 'backend', 'api-checks');
  }, [fetchScopeNotes]);

  useEffect(() => {
    fetchCategories();
    fetchHistory();
    fetchPytestSuites();
    fetchAutoPipelines();
  }, [fetchCategories, fetchHistory, fetchPytestSuites, fetchAutoPipelines]);

  // Refresh auto-pipeline cards every time the user switches to the
  // Regressions tab so the "last X minutes ago" timestamps stay fresh
  // without polling continuously.
  useEffect(() => {
    if (activeTab === 'regressions') {
      fetchAutoPipelines();
    }
  }, [activeTab, fetchAutoPipelines]);

  const runCategory = async (categoryId: string) => {
    setRunning(prev => ({ ...prev, [categoryId]: true }));
    setExpanded(prev => ({ ...prev, [categoryId]: true }));

    const cat = categories.find(c => c.id === categoryId);
    const execution = (cat as any)?.execution || 'frontend';

    try {
      if (execution === 'backend') {
        // DB-only tests: run on server
        const cboId = targetCboId;
        const r = await fetch(
          `${apiUrl}/api/admin/tests/${categoryId}/run?cbo_id=${cboId}`,
          { method: 'POST', headers: getHeaders() }
        );
        if (r.ok) {
          const data = await r.json();
          setResults(prev => ({ ...prev, [categoryId]: data }));
        }
      } else {
        // HTTP tests: execute from browser via fetch, report results back
        // Use preloaded check defs or fetch them
        let defs = checkDefs[categoryId];
        if (!defs) {
          const checksR = await fetch(`${apiUrl}/api/admin/tests/${categoryId}/checks`, { headers: getHeaders() });
          if (!checksR.ok) return;
          defs = (await checksR.json()).checks || [];
        }

        const cboId = targetCboId;
        const unique = Math.random().toString(36).substring(2, 8);
        const futureDate = new Date(Date.now() + 7 * 86400000);
        const futureEnd = new Date(Date.now() + 7 * 86400000 + 3600000);
        const startTime = performance.now();
        const checkResults: TestCheck[] = [];

        // Show initial "pending" state for all checks
        const pendingChecks: TestCheck[] = defs.map((d: any) => ({
          name: d.name, status: 'pending' as any, detail: d.description || 'Waiting...', duration_ms: 0,
        }));
        setResults(prev => ({ ...prev, [categoryId]: {
          category: categoryId, status: 'running' as any, checks: pendingChecks,
          total: defs.length, passed: 0, failed: 0, duration_ms: 0,
        }}));

        for (let i = 0; i < defs.length; i++) {
          const def = defs[i];
          const checkStart = performance.now();
          try {
            let path = (def.path as string).replace('{cbo_id}', cboId).replace('{unique}', unique);
            let body = def.body ? JSON.parse(
              JSON.stringify(def.body)
                .replace(/\{unique\}/g, unique)
                .replace(/\{future_iso\}/g, futureDate.toISOString())
                .replace(/\{future_end_iso\}/g, futureEnd.toISOString())
            ) : undefined;

            const fetchOpts: RequestInit = {
              method: def.method,
              headers: def.auth_required === false ? { 'Content-Type': 'application/json' } : getHeaders(),
            };
            if (body && def.method !== 'GET') fetchOpts.body = JSON.stringify(body);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 15000);
            fetchOpts.signal = controller.signal;
            const r = await fetch(`${apiUrl}${path}`, fetchOpts);
            clearTimeout(timeoutId);
            const elapsed = Math.round(performance.now() - checkStart);
            let status: 'pass' | 'fail' | 'error' = 'fail';
            let detail = `HTTP ${r.status}`;

            if (def.validate && r.ok) {
              const data = await r.json();
              if (def.validate === 'has_data') {
                const hasData = Array.isArray(data) ? data.length > 0 :
                  (data.total > 0 || data.members?.length > 0 || data.tasks?.length > 0 || Object.keys(data).length > 2);
                status = hasData ? 'pass' : 'fail';
                detail = hasData ? `HTTP ${r.status} (has data)` : `HTTP ${r.status} (empty)`;
              } else if (def.validate === 'schema_paths_gt_50') {
                const paths = Object.keys(data.paths || {}).length;
                status = paths > 50 ? 'pass' : 'fail';
                detail = `${paths} paths`;
              } else if (def.validate === 'critical_routes') {
                const criticalRoutes = ['/api/members', '/api/programs', '/api/tasks', '/api/referrals/', '/api/forms/templates/', '/api/campaigns', '/api/workflows/templates', '/api/emergency/incidents', '/api/auth/login', '/api/calendar/events'];
                const missing = criticalRoutes.filter(rt => !(rt in (data.paths || {})));
                status = missing.length === 0 ? 'pass' : 'fail';
                detail = missing.length === 0 ? `All ${criticalRoutes.length} routes present` : `Missing: ${missing.join(', ')}`;
              } else if (def.validate === 'schema_models_gt_20') {
                const count = Object.keys(data.components?.schemas || {}).length;
                status = count > 20 ? 'pass' : 'fail';
                detail = `${count} models`;
              } else { status = 'pass'; }
            } else if (def.expected_status) {
              status = r.status === def.expected_status ? 'pass' : 'fail';
            } else { status = r.ok ? 'pass' : 'fail'; }

            checkResults.push({ name: def.name, status, detail, duration_ms: elapsed });
          } catch (err: any) {
            const elapsed = Math.round(performance.now() - checkStart);
            checkResults.push({ name: def.name, status: 'error', detail: err.message?.substring(0, 200) || 'Unknown error', duration_ms: elapsed });
          }

          // Update results after each check (live progress)
          const completedSoFar = [...checkResults];
          const remainingPending = defs.slice(i + 1).map((d: any) => ({
            name: d.name, status: 'pending' as any, detail: d.description || 'Waiting...', duration_ms: 0,
          }));
          const passed = completedSoFar.filter(c => c.status === 'pass').length;
          setResults(prev => ({ ...prev, [categoryId]: {
            category: categoryId, status: 'running' as any,
            checks: [...completedSoFar, ...remainingPending],
            total: defs.length, passed, failed: completedSoFar.length - passed,
            duration_ms: Math.round(performance.now() - startTime),
          }}));
        }

        const totalDuration = Math.round(performance.now() - startTime);
        const passed = checkResults.filter(c => c.status === 'pass').length;
        const result: TestResult = {
          category: categoryId,
          status: passed === checkResults.length ? 'pass' : 'fail',
          checks: checkResults,
          total: checkResults.length,
          passed,
          failed: checkResults.length - passed,
          duration_ms: totalDuration,
        };

        setResults(prev => ({ ...prev, [categoryId]: result }));

        // Report results back to backend for storage
        try {
          await fetch(`${apiUrl}/api/admin/tests/results`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ ...result, cbo_id: parseInt(cboId) || undefined }),
          });
        } catch { /* non-critical */ }
      }

      fetchCategories();
      fetchHistory();
    } catch (err) {
      console.error(`Failed to run ${categoryId} tests:`, err);
    } finally {
      setRunning(prev => ({ ...prev, [categoryId]: false }));
    }
  };

  const runAllTests = async () => {
    for (const cat of categories) {
      await runCategory(cat.id);
    }
  };

  const runEngagementEmailSendRegression = async () => {
    setRegressionRunning(true);
    try {
      const r = await fetch(
        `${apiUrl}/api/admin/tests/regression/engagement-email-send/run`,
        { method: 'POST', headers: getHeaders() },
      );
      if (r.ok) {
        setRegressionResult(await r.json());
      } else {
        const errText = await r.text().catch(() => '');
        setRegressionResult({
          category: 'engagement-email-send-regression',
          status: 'error',
          checks: [{ name: 'http_request', status: 'error', detail: `HTTP ${r.status}: ${errText}`, duration_ms: 0 }],
          total: 1, passed: 0, failed: 1, duration_ms: 0,
        });
      }
    } catch (err) {
      setRegressionResult({
        category: 'engagement-email-send-regression',
        status: 'error',
        checks: [{ name: 'http_request', status: 'error', detail: `Network error: ${err}`, duration_ms: 0 }],
        total: 1, passed: 0, failed: 1, duration_ms: 0,
      });
    } finally {
      setRegressionRunning(false);
    }
  };

  const runPhoneChange2faRegression = async () => {
    setPhoneChange2faRunning(true);
    try {
      const r = await fetch(
        `${apiUrl}/api/admin/tests/regression/phone-change-2fa/run`,
        { method: 'POST', headers: getHeaders() },
      );
      if (r.ok) {
        setPhoneChange2faResult(await r.json());
      } else {
        const errText = await r.text().catch(() => '');
        setPhoneChange2faResult({
          category: 'phone-change-2fa-regression',
          status: 'error',
          checks: [{ name: 'http_request', status: 'error', detail: `HTTP ${r.status}: ${errText}`, duration_ms: 0 }],
          total: 1, passed: 0, failed: 1, duration_ms: 0,
        });
      }
    } catch (err) {
      setPhoneChange2faResult({
        category: 'phone-change-2fa-regression',
        status: 'error',
        checks: [{ name: 'http_request', status: 'error', detail: `Network error: ${err}`, duration_ms: 0 }],
        total: 1, passed: 0, failed: 1, duration_ms: 0,
      });
    } finally {
      setPhoneChange2faRunning(false);
    }
  };

  const runFormFieldSyncRegression = async () => {
    setFormFieldSyncRunning(true);
    try {
      const r = await fetch(
        `${apiUrl}/api/admin/tests/regression/form-field-sync/run`,
        { method: 'POST', headers: getHeaders() },
      );
      if (r.ok) {
        setFormFieldSyncResult(await r.json());
      } else {
        const errText = await r.text().catch(() => '');
        setFormFieldSyncResult({
          category: 'form-field-sync-regression',
          status: 'error',
          checks: [{ name: 'http_request', status: 'error', detail: `HTTP ${r.status}: ${errText}`, duration_ms: 0 }],
          total: 1, passed: 0, failed: 1, duration_ms: 0,
        });
      }
    } catch (err) {
      setFormFieldSyncResult({
        category: 'form-field-sync-regression',
        status: 'error',
        checks: [{ name: 'http_request', status: 'error', detail: `Network error: ${err}`, duration_ms: 0 }],
        total: 1, passed: 0, failed: 1, duration_ms: 0,
      });
    } finally {
      setFormFieldSyncRunning(false);
    }
  };

  const runFieldConfigRegression = async () => {
    setFieldConfigRunning(true);
    try {
      const r = await fetch(
        `${apiUrl}/api/admin/tests/regression/field-config/run`,
        { method: 'POST', headers: getHeaders() },
      );
      if (r.ok) {
        setFieldConfigResult(await r.json());
      } else {
        const errText = await r.text().catch(() => '');
        setFieldConfigResult({
          category: 'field-config-regression',
          status: 'error',
          checks: [{ name: 'http_request', status: 'error', detail: `HTTP ${r.status}: ${errText}`, duration_ms: 0 }],
          total: 1, passed: 0, failed: 1, duration_ms: 0,
        });
      }
    } catch (err) {
      setFieldConfigResult({
        category: 'field-config-regression',
        status: 'error',
        checks: [{ name: 'http_request', status: 'error', detail: `Network error: ${err}`, duration_ms: 0 }],
        total: 1, passed: 0, failed: 1, duration_ms: 0,
      });
    } finally {
      setFieldConfigRunning(false);
    }
  };

  const totalPassed = Object.values(results).reduce((sum, r) => sum + r.passed, 0);
  const totalFailed = Object.values(results).reduce((sum, r) => sum + r.failed, 0);
  const totalChecks = Object.values(results).reduce((sum, r) => sum + r.total, 0);
  const anyRunning = Object.values(running).some(Boolean);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-600">Loading test categories...</span>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[13px] text-ink-500">Run API endpoint checks (browser-side) and pytest domain suites (server-side) with coverage reports</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Organization Selector */}
          <select
            value={targetCboId}
            onChange={(e) => { setTargetCboId(e.target.value); setResults({}); }}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          >
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}{org.is_sandbox ? ' (Sandbox)' : ''}
              </option>
            ))}
            {orgs.length === 0 && <option value={targetCboId}>Org {targetCboId}</option>}
          </select>
          <button
            onClick={() => { fetchCategories(); fetchHistory(); }}
            className="flex items-center gap-2 px-3 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
          <button
            onClick={runAllTests}
            disabled={anyRunning}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {anyRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
            Run All Tests
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {totalChecks > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">Total Checks</p>
            <p className="text-2xl font-bold text-gray-900">{totalChecks}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">Passed</p>
            <p className="text-2xl font-bold text-green-600">{totalPassed}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">Failed</p>
            <p className="text-2xl font-bold text-red-600">{totalFailed}</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-sm text-gray-500">Pass Rate</p>
            <p className="text-2xl font-bold text-gray-900">
              {totalChecks > 0 ? Math.round((totalPassed / totalChecks) * 100) : 0}%
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-6">
          <button
            onClick={() => setActiveTab('tests')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tests' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2"><PlayCircle className="w-4 h-4" /> API Endpoint Checks</span>
          </button>
          <button
            onClick={() => setActiveTab('pytest')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'pytest' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2"><FlaskConical className="w-4 h-4" /> Pytest Domain Suites</span>
          </button>
          <button
            onClick={() => { setActiveTab('scope-notes'); fetchScopeReport(); }}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'scope-notes' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Scope Notes</span>
          </button>
          <button
            onClick={() => setActiveTab('regressions')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'regressions' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Custom Regressions</span>
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`py-3 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'history' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <span className="flex items-center gap-2"><History className="w-4 h-4" /> Run History</span>
          </button>
        </nav>
      </div>

      {/* Test Suites Tab */}
      {activeTab === 'tests' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
            <strong>API Endpoint Checks</strong> run from your browser via HTTP fetch against the live API.
            They validate endpoint reachability, response shapes, and data visibility for your selected organization.
            For deeper server-side integration tests (CRUD workflows, state machines, DB transactions), use the <strong>Pytest Domain Suites</strong> tab.
          </div>
          {categories.map((cat) => {
            const result = results[cat.id];
            const isRunning = running[cat.id];
            const isExpanded = expanded[cat.id];
            const style = result ? STATUS_STYLES[result.status] || STATUS_STYLES.error : null;

            return (
              <div key={cat.id} className={`bg-white rounded-lg border ${style ? style.bg : 'border-gray-200'} overflow-hidden`}>
                {/* Category Header */}
                <div className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => {
                        const willOpen = !expanded[cat.id];
                        setExpanded(prev => ({ ...prev, [cat.id]: !prev[cat.id] }));
                        if (willOpen) openApiCheckScope(cat.id);
                      }}
                      className="hover:text-gray-600 text-gray-500"
                    >
                      {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                    </button>
                    <div className="text-indigo-600">{CATEGORY_ICONS[cat.id] || <PlayCircle className="w-5 h-5" />}</div>
                    <div>
                      <h3 className="font-medium text-gray-900">{cat.name}</h3>
                      <p className="text-sm text-gray-500">{cat.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {/* Last run info */}
                    {cat.last_run && !result && (
                      <div className="text-right text-sm">
                        <div className="flex items-center gap-1">
                          {STATUS_STYLES[cat.last_run.status]?.icon}
                          <span className={STATUS_STYLES[cat.last_run.status]?.text || 'text-gray-500'}>
                            {cat.last_run.passed}/{cat.last_run.total} passed
                          </span>
                        </div>
                        <div className="text-gray-400 text-xs">
                          {new Date(cat.last_run.timestamp).toLocaleString()}
                        </div>
                      </div>
                    )}
                    {/* Current result */}
                    {result && (
                      <div className="text-right text-sm">
                        <div className="flex items-center gap-1">
                          {style?.icon}
                          <span className={style?.text || ''}>
                            {result.passed}/{result.total} passed
                          </span>
                        </div>
                        <div className="text-gray-400 text-xs">{result.duration_ms}ms</div>
                      </div>
                    )}
                    <button
                      onClick={() => runCategory(cat.id)}
                      disabled={isRunning}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                    >
                      {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                      {isRunning ? 'Running...' : 'Run'}
                    </button>
                  </div>
                </div>

                {/* Expanded: show preloaded checks, live progress, or results + scope notes */}
                {isExpanded && (
                  <>
                    {(() => {
                      // Determine what checks to display
                      const displayChecks = result?.checks || (checkDefs[cat.id] || []).map((d: any) => ({
                        name: d.name, status: 'pending', detail: `${d.method || ''} ${d.path || ''} — ${d.description || ''}`, duration_ms: 0,
                      }));
                      if (displayChecks.length === 0 && (cat as any).execution === 'backend') {
                        return (
                          <div className="border-t border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-400">
                            Click <strong>Run</strong> to execute server-side checks
                          </div>
                        );
                      }
                      return (
                      <div className="border-t border-gray-200 bg-gray-50">
                        {!result && (
                          <div className="px-4 pt-2 text-xs text-gray-500 flex items-center gap-1">
                            <ListTree className="w-3.5 h-3.5" /> Scope — {displayChecks.length} check{displayChecks.length === 1 ? '' : 's'} this runs (click Run to execute)
                          </div>
                        )}
                        <table className="w-full text-sm">
                          <thead className="bg-gray-100">
                            <tr>
                              <th className="text-left px-4 py-2 font-medium text-gray-600">Check</th>
                              <th className="text-left px-4 py-2 font-medium text-gray-600">Status</th>
                              <th className="text-left px-4 py-2 font-medium text-gray-600">Detail</th>
                              <th className="text-right px-4 py-2 font-medium text-gray-600">Time</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {displayChecks.map((check: any, i: number) => (
                              <tr key={i} className={
                                check.status === 'fail' ? 'bg-red-50' :
                                check.status === 'error' ? 'bg-yellow-50' :
                                check.status === 'pending' ? 'bg-white' : ''
                              }>
                                <td className="px-4 py-2 font-mono text-xs text-gray-800">{check.name}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                    check.status === 'pass' ? 'bg-green-100 text-green-700' :
                                    check.status === 'fail' ? 'bg-red-100 text-red-700' :
                                    check.status === 'pending' ? 'bg-gray-100 text-gray-400' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {check.status === 'pass' ? <CheckCircle className="w-3 h-3" /> :
                                     check.status === 'fail' ? <XCircle className="w-3 h-3" /> :
                                     check.status === 'pending' ? <Clock className="w-3 h-3" /> :
                                     <AlertTriangle className="w-3 h-3" />}
                                    {check.status}
                                  </span>
                                </td>
                                <td className="px-4 py-2 text-gray-600 text-xs">{check.detail}</td>
                                <td className="px-4 py-2 text-right text-gray-400 text-xs">{check.duration_ms}ms</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      );
                    })()}
                    <ScopeNotesPanel
                      suiteId={cat.id} repo="backend" surface="api-checks"
                      notes={scopeNotes[scopeKey('api-checks', cat.id)] || []}
                      loading={!!scopeNotesLoading[scopeKey('api-checks', cat.id)]}
                      scopeNodeids={(checkDefs[cat.id] || []).map((d: any) => d.name)}
                      onAdd={(title, description) => createScopeNote(cat.id, 'backend', 'api-checks', title, description)}
                      onUpdate={(id, patch) => updateScopeNote(id, patch, cat.id, 'backend', 'api-checks')}
                      onDelete={(id) => deleteScopeNote(id, cat.id, 'backend', 'api-checks')}
                      onSuggest={() => suggestScopeGaps(cat.id, 'backend', 'api-checks')}
                      onDraft={(id) => draftScopeNoteCode(id, cat.id, 'backend', 'api-checks')}
                      suggesting={!!scopeSuggesting[scopeKey('api-checks', cat.id)]}
                      draftingIds={scopeDrafting}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Pytest Domain Suites Tab */}
      {activeTab === 'pytest' && (
        <div className="space-y-6">
          <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 text-sm text-purple-700">
            <strong>Pytest Domain Suites — on-demand against the live production backend.</strong>{' '}
            These run inside the deployed <code className="bg-purple-100 px-1 rounded">weavein-api</code> service via
            subprocess, so they hit the real Render Postgres. Use this when you need to verify a specific suite against
            <em>current prod data</em> after a deploy or schema change. Each suite runs in a rolled-back transactional
            session — no writes persist.
            <div className="mt-2">
              The same suites also run every night in CI against a clean Postgres + seed (GitHub Actions →
              <code className="bg-purple-100 px-1 rounded mx-1">nightly-tests.yml</code>); see the "Nightly (GitHub
              Actions)" cards on the <strong>Custom Regressions</strong> tab for last night's status. This tab is for
              when "code is green but did it work against prod?" matters.
            </div>
            <div className="mt-2 text-xs">
              Suites marked <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700 mx-0.5">prod-safe</span>
              don't require DB access and are safe to run anywhere.
            </div>
          </div>
          {/* Suite Groups — quick actions */}
          {pytestGroups.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {pytestGroups.map((group) => {
                const groupRunning = group.suites.some(s => pytestRunning[s]);
                return (
                  <div key={group.id} className="bg-white rounded-lg border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-medium text-gray-900 text-sm">{group.name}</h3>
                      <button
                        onClick={() => runPytestGroup(group.id)}
                        disabled={groupRunning}
                        className="flex items-center gap-1 px-2.5 py-1 text-xs bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {groupRunning ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
                        Run Group
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">{group.description}</p>
                    <div className="flex flex-wrap gap-1">
                      {group.suites.map(s => (
                        <span key={s} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${
                          pytestResults[s]?.status === 'pass' ? 'bg-green-100 text-green-700' :
                          pytestResults[s]?.status === 'fail' ? 'bg-red-100 text-red-700' :
                          pytestRunning[s] ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {SUITE_ICONS[s] || <FlaskConical className="w-3 h-3" />}
                          {s}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pytest suite summary cards */}
          {Object.keys(pytestResults).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg border p-4">
                <p className="text-sm text-gray-500">Total Tests</p>
                <p className="text-2xl font-bold text-gray-900">
                  {Object.values(pytestResults).reduce((s, r) => s + r.total, 0)}
                </p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-sm text-gray-500">Passed</p>
                <p className="text-2xl font-bold text-green-600">
                  {Object.values(pytestResults).reduce((s, r) => s + r.passed, 0)}
                </p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-sm text-gray-500">Failed</p>
                <p className="text-2xl font-bold text-red-600">
                  {Object.values(pytestResults).reduce((s, r) => s + r.failed, 0)}
                </p>
              </div>
              <div className="bg-white rounded-lg border p-4">
                <p className="text-sm text-gray-500">Skipped</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {Object.values(pytestResults).reduce((s, r) => s + r.skipped, 0)}
                </p>
              </div>
            </div>
          )}

          {/* Individual suites */}
          <div className="space-y-3">
            {pytestSuites.map((suite) => {
              const result = pytestResults[suite.id];
              const isRunning = pytestRunning[suite.id];
              const isExpanded = pytestExpanded[suite.id];
              const style = result ? STATUS_STYLES[result.status] || STATUS_STYLES.error : null;

              return (
                <div key={suite.id} className={`bg-white rounded-lg border ${style ? style.bg : 'border-gray-200'} overflow-hidden`}>
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          const willOpen = !pytestExpanded[suite.id];
                          setPytestExpanded(prev => ({ ...prev, [suite.id]: !prev[suite.id] }));
                          if (willOpen) openPytestScope(suite.id);
                        }}
                        className="hover:text-gray-600 text-gray-500"
                      >
                        {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                      </button>
                      <div className="text-indigo-600">{SUITE_ICONS[suite.id] || <FlaskConical className="w-5 h-5" />}</div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-gray-900">{suite.name}</h3>
                          {suite.safe_for_production && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">prod-safe</span>
                          )}
                          {!suite.requires_db && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">no-db</span>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">{suite.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      {result && (
                        <div className="text-right text-sm">
                          <div className="flex items-center gap-1">
                            {style?.icon}
                            <span className={style?.text || ''}>
                              {result.passed}/{result.total} passed
                              {result.skipped > 0 && ` (${result.skipped} skipped)`}
                            </span>
                          </div>
                          <div className="text-gray-400 text-xs">
                            {result.duration_ms > 1000 ? `${(result.duration_ms / 1000).toFixed(1)}s` : `${result.duration_ms}ms`}
                            {result.pass_rate && ` — ${result.pass_rate}`}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={() => runPytestSuite(suite.id)}
                        disabled={isRunning}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {isRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />}
                        {isRunning ? 'Running...' : 'Run'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded: show individual test results */}
                  {isExpanded && result && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      {/* Per-test results table */}
                      {result.checks && result.checks.length > 0 && (
                        <div className="max-h-96 overflow-y-auto">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-100 sticky top-0">
                              <tr>
                                <th className="text-left px-4 py-2 font-medium text-gray-600">Test</th>
                                <th className="text-left px-4 py-2 font-medium text-gray-600 w-20">Status</th>
                                <th className="text-left px-4 py-2 font-medium text-gray-600">Detail</th>
                                <th className="text-right px-4 py-2 font-medium text-gray-600 w-16">Time</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                              {result.checks.map((check: any, i: number) => (
                                <tr key={i} className={
                                  check.status === 'fail' ? 'bg-red-50' :
                                  check.status === 'skip' ? 'bg-yellow-50' :
                                  ''
                                }>
                                  <td className="px-4 py-1.5 font-mono text-xs text-gray-800">{check.name}</td>
                                  <td className="px-4 py-1.5">
                                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                                      check.status === 'pass' ? 'bg-green-100 text-green-700' :
                                      check.status === 'fail' ? 'bg-red-100 text-red-700' :
                                      check.status === 'skip' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-gray-100 text-gray-500'
                                    }`}>
                                      {check.status === 'pass' ? <CheckCircle className="w-3 h-3" /> :
                                       check.status === 'fail' ? <XCircle className="w-3 h-3" /> :
                                       check.status === 'skip' ? <AlertTriangle className="w-3 h-3" /> :
                                       <Clock className="w-3 h-3" />}
                                      {check.status}
                                    </span>
                                  </td>
                                  <td className="px-4 py-1.5 text-xs text-gray-600 max-w-md truncate" title={check.detail}>
                                    {check.detail}
                                  </td>
                                  <td className="px-4 py-1.5 text-right text-gray-400 text-xs">
                                    {check.duration_ms > 0 ? `${check.duration_ms}ms` : ''}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}

                      {/* Batch summary (shown below test table when batches exist) */}
                      {result.batches && Object.keys(result.batches).length > 0 && (
                        <div className="px-4 py-3 border-t border-gray-200">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Batch Summary</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {Object.entries(result.batches).map(([batchName, stats]) => (
                              <div key={batchName} className={`flex items-center justify-between px-3 py-2 rounded border text-xs ${
                                stats.failed === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                              }`}>
                                <span className="font-mono text-gray-800 truncate mr-2">{batchName}</span>
                                <span className={stats.failed === 0 ? 'text-green-700' : 'text-red-700'}>
                                  {stats.passed}/{stats.total} ({stats.pass_rate})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Raw output fallback */}
                      {result.output && (!result.checks || result.checks.length === 0) && (
                        <div className="px-4 py-3">
                          <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Output</h4>
                          <pre className="text-xs text-gray-700 bg-gray-100 rounded p-3 max-h-60 overflow-y-auto whitespace-pre-wrap">
                            {result.output}
                          </pre>
                        </div>
                      )}

                      {/* Error message */}
                      {result.error && (
                        <div className="px-4 py-3 bg-red-50">
                          <p className="text-sm text-red-700">{result.error}</p>
                        </div>
                      )}

                      {/* Empty state */}
                      {!result.batches && !result.output && !result.error && result.checks?.length === 0 && (
                        <div className="px-4 py-6 text-center text-sm text-gray-400">
                          No detailed results available for this run.
                        </div>
                      )}
                    </div>
                  )}

                  {/* Expanded, not yet run: show the suite's scope (the actual tests it covers) */}
                  {isExpanded && !result && !isRunning && (
                    <div className="border-t border-gray-200 bg-gray-50">
                      <ScopeTestList
                        loading={!!suiteScopeLoading[suite.id]}
                        markerFilter={suiteScope[suite.id]?.marker_filter}
                        emptyHint="No tests found in this suite's files."
                        items={(suiteScope[suite.id]?.tests || []).map(t => ({
                          label: t.classname ? `${t.classname}::${t.name}` : t.name,
                          detail: t.docstring,
                          meta: `${t.file.split('/').pop()}:${t.line}`,
                          markers: t.markers,
                        }))}
                      />
                    </div>
                  )}

                  {/* Running indicator */}
                  {isExpanded && isRunning && (
                    <div className="border-t border-gray-200 bg-blue-50 px-4 py-6 text-center text-sm text-blue-600">
                      <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
                      Running pytest suite... this may take a few minutes.
                    </div>
                  )}

                  {/* Documented scope notes — always available when expanded */}
                  {isExpanded && (
                    <ScopeNotesPanel
                      suiteId={suite.id} repo="backend" surface="pytest"
                      notes={scopeNotes[scopeKey('pytest', suite.id)] || []}
                      loading={!!scopeNotesLoading[scopeKey('pytest', suite.id)]}
                      scopeNodeids={(suiteScope[suite.id]?.tests || []).map(t => t.nodeid)}
                      onAdd={(title, description) => createScopeNote(suite.id, 'backend', 'pytest', title, description)}
                      onUpdate={(id, patch) => updateScopeNote(id, patch, suite.id, 'backend', 'pytest')}
                      onDelete={(id) => deleteScopeNote(id, suite.id, 'backend', 'pytest')}
                      onSuggest={() => suggestScopeGaps(suite.id, 'backend', 'pytest')}
                      onDraft={(id) => draftScopeNoteCode(id, suite.id, 'backend', 'pytest')}
                      suggesting={!!scopeSuggesting[scopeKey('pytest', suite.id)]}
                      draftingIds={scopeDrafting}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Coverage & Results Tab */}
      {activeTab === 'scope-notes' && (
        <div className="space-y-6">
          <div className="flex items-start justify-between gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800 flex-1">
              <strong>Documented test scope — dev report.</strong> Every test users (or AI) flagged as missing, across all suites,
              with implementation status. Add ideas from the <strong>Pytest Domain Suites</strong> / <strong>API Endpoint Checks</strong> tabs;
              mark a note <em>codified</em> and link its nodeid once the real test ships — it then runs in the runner &amp; nightly automatically.
            </div>
            <button onClick={fetchScopeReport} className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 shrink-0">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          {scopeReportLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600" />
              <span className="ml-2 text-gray-500">Loading scope report…</span>
            </div>
          )}

          {!scopeReportLoading && scopeReport && (
            <>
              {/* Rollup cards */}
              <div className="grid grid-cols-3 sm:grid-cols-7 gap-3">
                {[
                  { key: 'total', label: 'Total', cls: 'text-gray-900 bg-gray-50' },
                  { key: 'proposed', label: 'Proposed', cls: 'text-amber-700 bg-amber-50' },
                  { key: 'codified', label: 'Codified', cls: 'text-green-700 bg-green-50' },
                  { key: 'covered', label: 'Covered', cls: 'text-green-700 bg-green-50' },
                  { key: 'wont_do', label: "Won't do", cls: 'text-gray-500 bg-gray-50' },
                  { key: 'ai', label: 'AI-suggested', cls: 'text-purple-700 bg-purple-50' },
                  { key: 'with_draft', label: 'Has draft', cls: 'text-indigo-700 bg-indigo-50' },
                ].map(c => (
                  <div key={c.key} className={`text-center p-3 rounded-lg ${c.cls}`}>
                    <p className="text-2xl font-bold">{scopeReport.totals[c.key] || 0}</p>
                    <p className="text-xs">{c.label}</p>
                  </div>
                ))}
              </div>

              {scopeReport.suites.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                  <Lightbulb className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <h3 className="text-lg font-medium text-gray-700 mb-1">No scope notes yet</h3>
                  <p className="text-sm text-gray-500">Open a suite on the Pytest or API Endpoint Checks tab and add a test idea (or use &quot;Suggest missing tests (AI)&quot;).</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {scopeReport.suites.map(suite => (
                    <div key={`${suite.surface}:${suite.suite_id}`} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="font-medium text-gray-900 truncate">{suite.name}</span>
                          <span className="px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-600 shrink-0">{suite.surface}</span>
                          <span className="text-xs text-gray-400 font-mono truncate">{suite.suite_id}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-xs shrink-0">
                          <span className="px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{suite.counts.proposed || 0} proposed</span>
                          <span className="px-1.5 py-0.5 rounded bg-green-100 text-green-700">{suite.counts.codified || 0} codified</span>
                          {(suite.counts.wont_do || 0) > 0 && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">{suite.counts.wont_do} won&apos;t do</span>}
                        </div>
                      </div>
                      <div className="divide-y divide-gray-100">
                        {suite.notes.map(note => {
                          const covered = note.status === 'codified' && !!note.linked_nodeid;
                          return (
                            <div key={note.id} className="px-4 py-2.5">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-medium text-gray-900">{note.title}</span>
                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${NOTE_STATUS_STYLES[note.status]}`}>{note.status}</span>
                                {note.origin === 'ai' && <span className="px-1.5 py-0.5 rounded text-[10px] bg-purple-50 text-purple-600 flex items-center gap-0.5"><Sparkles className="w-2.5 h-2.5" /> AI</span>}
                                {note.draft_code && <span className="px-1.5 py-0.5 rounded text-[10px] bg-indigo-50 text-indigo-600 flex items-center gap-0.5"><FileCode className="w-2.5 h-2.5" /> draft</span>}
                                {covered && <span className="px-1.5 py-0.5 rounded text-[10px] bg-green-100 text-green-700">✓ covered</span>}
                              </div>
                              <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">{note.description}</p>
                              {note.linked_nodeid && <p className="text-[11px] text-gray-400 mt-0.5 font-mono flex items-center gap-1"><Link2 className="w-3 h-3" />{note.linked_nodeid}</p>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Custom Regressions Tab */}
      {activeTab === 'regressions' && (
        <div className="space-y-4">
          {/* ─── Auto Pipeline summary — one card per (repo, source) ─── */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div>
                <h3 className="font-medium text-gray-900">Auto Test Pipeline</h3>
                <p className="text-xs text-gray-500">
                  Latest run per automated guard rail. Stop Hook fires every session; pre-push gates pushes;
                  nightly runs full vitest + pytest on a schedule.
                </p>
              </div>
              <button
                onClick={fetchAutoPipelines}
                disabled={autoPipelinesLoading}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
              >
                {autoPipelinesLoading
                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5" />}
                Refresh
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 p-4">
              {(['stop_hook', 'pre_push', 'nightly', 'deploy_gate'] as const).map(source => (
                <div key={source} className="border border-gray-200 rounded-md p-3 bg-gray-50">
                  <div className="text-xs font-medium text-gray-700 mb-2">{PIPELINE_LABELS[source]}</div>
                  {(['backend', 'frontend'] as const).map(repo => {
                    const entry = autoPipelines.find(p => p.repo === repo && p.source === source);
                    const run = entry?.last_run;
                    const s = run ? (STATUS_STYLES[run.status] || STATUS_STYLES.error) : null;
                    return (
                      <div key={repo} className="mb-2 last:mb-0">
                        <div className="flex items-center justify-between gap-2 text-xs">
                          <span className="font-mono text-gray-500">{repo}</span>
                          {run
                            ? <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] ${run.status === 'pass' ? 'bg-green-100 text-green-700' : run.status === 'fail' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                                {s?.icon} {run.passed}/{run.total}
                              </span>
                            : <span className="text-[11px] text-gray-400 italic">no runs yet</span>}
                        </div>
                        {run && (
                          <div className="mt-1 flex items-center justify-between gap-2 text-[11px] text-gray-500">
                            {run.commit_sha
                              ? <span className="font-mono">{run.commit_sha.slice(0, 7)}</span>
                              : <span>—</span>}
                            <span>{new Date(run.timestamp).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            <strong>Custom Regressions</strong> are one-off integration checks for specific past bugs.
            They exercise full code paths server-side (no Playwright/browser needed) and may have side effects —
            see each card. Use these to verify a fix manually after deploy.
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="text-indigo-600 mt-1"><MessageSquare className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium text-gray-900">Engagement Email Send (round-trip)</h3>
                  <p className="text-sm text-gray-500 max-w-2xl">
                    Regression for the May 2026 schema-drift bug: <code>/api/campaigns/engagements/&#123;id&#125;/send-individual</code>{' '}
                    silently returned <code>status="queued"</code> for email without calling SendGrid, and the{' '}
                    <code>member_communications</code> INSERT referenced columns that don't exist.
                    Creates a temp engagement, sends to member 188 (vingulati1@gmail.com by default),
                    polls history for the row, deletes the engagement.
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    <strong>Side effect:</strong> sends one real email per run via SendGrid.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {regressionResult && (
                  <div className="text-right text-sm">
                    <div className="flex items-center gap-1 justify-end">
                      {STATUS_STYLES[regressionResult.status]?.icon}
                      <span className={STATUS_STYLES[regressionResult.status]?.text || ''}>
                        {regressionResult.passed}/{regressionResult.total} passed
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs">{regressionResult.duration_ms}ms</div>
                  </div>
                )}
                <button
                  onClick={runEngagementEmailSendRegression}
                  disabled={regressionRunning}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {regressionRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                  Run
                </button>
              </div>
            </div>

            {regressionResult && (
              <div className="border-t border-gray-200 divide-y divide-gray-100">
                {regressionResult.checks.map((c, i) => {
                  const s = STATUS_STYLES[c.status] || STATUS_STYLES.error;
                  return (
                    <div key={i} className={`px-4 py-3 flex items-start gap-3 ${s.bg}`}>
                      <div className="mt-0.5">{s.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${s.text}`}>{c.name}</div>
                        <div className="text-xs text-gray-600 mt-0.5 break-words">{c.detail}</div>
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">{c.duration_ms}ms</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 2FA self-service phone change (email-confirmation) regression */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="text-indigo-600 mt-1"><Lock className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium text-gray-900">2FA Phone Change — Email Confirmation (round-trip)</h3>
                  <p className="text-sm text-gray-500 max-w-2xl">
                    Regression for the self-service &ldquo;wrong number, change it&rdquo; flow at the 2FA login screen.
                    Verifies the security-critical invariant: <code>/login/update-phone-2fa</code> emails a 6-digit code
                    and stores a pending change <strong>without</strong> touching the number or sending any SMS, and that
                    <code> /login/confirm-phone-change</code> rejects a wrong code. Runs against a throwaway,
                    non-privileged user it creates and deletes.
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    <strong>Side effect:</strong> attempts one confirmation email to a non-deliverable
                    <code> @weavein.test</code> address (bounces). <strong>Sends no SMS</strong> — the positive
                    confirm-and-SMS path is covered by the mocked nightly pytest <code>TestPhoneChange2FA</code>.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {phoneChange2faResult && (
                  <div className="text-right text-sm">
                    <div className="flex items-center gap-1 justify-end">
                      {STATUS_STYLES[phoneChange2faResult.status]?.icon}
                      <span className={STATUS_STYLES[phoneChange2faResult.status]?.text || ''}>
                        {phoneChange2faResult.passed}/{phoneChange2faResult.total} passed
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs">{phoneChange2faResult.duration_ms}ms</div>
                  </div>
                )}
                <button
                  onClick={runPhoneChange2faRegression}
                  disabled={phoneChange2faRunning}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {phoneChange2faRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                  Run
                </button>
              </div>
            </div>

            {phoneChange2faResult && (
              <div className="border-t border-gray-200 divide-y divide-gray-100">
                {phoneChange2faResult.checks.map((c, i) => {
                  const s = STATUS_STYLES[c.status] || STATUS_STYLES.error;
                  return (
                    <div key={i} className={`px-4 py-3 flex items-start gap-3 ${s.bg}`}>
                      <div className="mt-0.5">{s.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${s.text}`}>{c.name}</div>
                        <div className="text-xs text-gray-600 mt-0.5 break-words">{c.detail}</div>
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">{c.duration_ms}ms</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Form-field-config auto-sync regression */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="text-indigo-600 mt-1"><FormInput className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium text-gray-900">Form Field-Config Auto-Sync (round-trip)</h3>
                  <p className="text-sm text-gray-500 max-w-2xl">
                    Regression for the May 2026 form-template field-config sync bug: edits to the master field
                    config (label, required flag, dropdown options) did NOT propagate to existing form
                    templates, and re-importing a field appended a duplicate instead of refreshing in place.
                    Seeds a temp custom field, creates a template with a stale snapshot, mutates the master,
                    saves the template again, and verifies the snapshot refreshed. Also verifies the
                    <code> sync_with_field_config = false</code> opt-out.
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    <strong>Side effect:</strong> creates &amp; deletes one <code>com_fields</code> row + one
                    <code> form_templates</code> row on the selected org. No external services.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {formFieldSyncResult && (
                  <div className="text-right text-sm">
                    <div className="flex items-center gap-1 justify-end">
                      {STATUS_STYLES[formFieldSyncResult.status]?.icon}
                      <span className={STATUS_STYLES[formFieldSyncResult.status]?.text || ''}>
                        {formFieldSyncResult.passed}/{formFieldSyncResult.total} passed
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs">{formFieldSyncResult.duration_ms}ms</div>
                  </div>
                )}
                <button
                  onClick={runFormFieldSyncRegression}
                  disabled={formFieldSyncRunning}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {formFieldSyncRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                  Run
                </button>
              </div>
            </div>

            {formFieldSyncResult && (
              <div className="border-t border-gray-200 divide-y divide-gray-100">
                {formFieldSyncResult.checks.map((c, i) => {
                  const s = STATUS_STYLES[c.status] || STATUS_STYLES.error;
                  return (
                    <div key={i} className={`px-4 py-3 flex items-start gap-3 ${s.bg}`}>
                      <div className="mt-0.5">{s.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${s.text}`}>{c.name}</div>
                        <div className="text-xs text-gray-600 mt-0.5 break-words">{c.detail}</div>
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">{c.duration_ms}ms</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* TRUS-59 Field Configuration regression */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="p-4 flex items-center justify-between gap-4">
              <div className="flex items-start gap-3">
                <div className="text-indigo-600 mt-1"><Sliders className="w-5 h-5" /></div>
                <div>
                  <h3 className="font-medium text-gray-900">Field Configuration (TRUS-59 round-trip)</h3>
                  <p className="text-sm text-gray-500 max-w-2xl">
                    Regression for the May 2026 Field Configuration bugs:
                    {' '}<strong>#1</strong> custom field <em>type</em> couldn't be edited (now mutable, with derive-from-existing-data);
                    {' '}<strong>#2</strong> drag-and-drop reorder silently failed when moving a field upward (collision detection swapped to <code>pointerWithin</code>);
                    {' '}<strong>#3</strong> Gender / Race / Ethnicity now have an editable per-org option list (defaults seeded in migration 382).
                    Seeds a temp custom field with a real value, flips its type to <code>select</code>,
                    round-trips a sort-order update on a core-field config, and overrides + clears Gender's option list.
                  </p>
                  <p className="text-xs text-amber-700 mt-2">
                    <strong>Side effect:</strong> creates &amp; deletes one <code>com_fields</code> row, one
                    <code> members_forms</code> value, and up to two <code>member_field_configurations</code> rows
                    on the selected org. No external services.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {fieldConfigResult && (
                  <div className="text-right text-sm">
                    <div className="flex items-center gap-1 justify-end">
                      {STATUS_STYLES[fieldConfigResult.status]?.icon}
                      <span className={STATUS_STYLES[fieldConfigResult.status]?.text || ''}>
                        {fieldConfigResult.passed}/{fieldConfigResult.total} passed
                      </span>
                    </div>
                    <div className="text-gray-400 text-xs">{fieldConfigResult.duration_ms}ms</div>
                  </div>
                )}
                <button
                  onClick={runFieldConfigRegression}
                  disabled={fieldConfigRunning}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {fieldConfigRunning ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
                  Run
                </button>
              </div>
            </div>

            {fieldConfigResult && (
              <div className="border-t border-gray-200 divide-y divide-gray-100">
                {fieldConfigResult.checks.map((c, i) => {
                  const s = STATUS_STYLES[c.status] || STATUS_STYLES.error;
                  return (
                    <div key={i} className={`px-4 py-3 flex items-start gap-3 ${s.bg}`}>
                      <div className="mt-0.5">{s.icon}</div>
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium ${s.text}`}>{c.name}</div>
                        <div className="text-xs text-gray-600 mt-0.5 break-words">{c.detail}</div>
                      </div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">{c.duration_ms}ms</div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600">Filter by source:</span>
            {[
              { value: '', label: 'All' },
              { value: 'stop_hook', label: 'Stop Hook' },
              { value: 'pre_push', label: 'Pre-Push' },
              { value: 'nightly', label: 'Nightly' },
              { value: 'deploy_gate', label: 'Deploy Gate' },
              { value: 'manual', label: 'Manual' },
            ].map(opt => (
              <button
                key={opt.value}
                onClick={() => setHistorySourceFilter(opt.value)}
                className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                  historySourceFilter === opt.value
                    ? 'bg-indigo-600 text-white border-indigo-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Category</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Source</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Results</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Duration</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Commit</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Triggered By</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      No test runs match the current filter.
                    </td>
                  </tr>
                ) : (
                  history.flatMap((run) => {
                    const isExpanded = !!historyExpanded[run.id];
                    const detail = historyDetail[run.id];
                    const isLoadingDetail = !!historyDetailLoading[run.id];
                    return [
                      <tr
                        key={run.id}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => toggleHistoryRow(run.id)}
                      >
                        <td className="px-4 py-3 font-medium text-gray-900">
                          <div className="flex items-center gap-2">
                            {isExpanded
                              ? <ChevronDown className="w-4 h-4 text-gray-400" />
                              : <ChevronRight className="w-4 h-4 text-gray-400" />}
                            {CATEGORY_ICONS[run.category] || SUITE_ICONS[run.category.replace('pytest-', '')] || <PlayCircle className="w-4 h-4 text-gray-400" />}
                            {run.category}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {run.source ? (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-700 font-mono">
                              {run.source}{run.repo && run.repo !== 'backend' ? `:${run.repo}` : ''}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            run.status === 'pass' ? 'bg-green-100 text-green-700' :
                            run.status === 'fail' ? 'bg-red-100 text-red-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {STATUS_STYLES[run.status]?.icon}
                            {run.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">{run.passed}/{run.total} passed</td>
                        <td className="px-4 py-3 text-gray-500">
                          {run.duration_ms > 1000 ? `${(run.duration_ms / 1000).toFixed(1)}s` : `${run.duration_ms}ms`}
                        </td>
                        <td className="px-4 py-3 text-xs font-mono text-gray-500">
                          {run.commit_sha ? run.commit_sha.slice(0, 7) : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{run.triggered_by_name || 'System'}</td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{new Date(run.timestamp).toLocaleString()}</td>
                      </tr>,
                      isExpanded && (
                        <tr key={`${run.id}-detail`} className="bg-gray-50">
                          <td colSpan={8} className="px-4 py-4">
                            {isLoadingDetail && !detail ? (
                              <div className="flex items-center gap-2 text-sm text-gray-500">
                                <Loader2 className="w-4 h-4 animate-spin" /> Loading run detail…
                              </div>
                            ) : detail ? (
                              <div className="space-y-4">
                                {/* Pytest session summary + per-batch breakdown (folded in from
                                    the former Coverage tab — shows for nightly/manual pytest runs) */}
                                {detail.pytest_summary && (
                                  <div className="bg-white border border-gray-200 rounded p-3">
                                    <div className="flex items-center gap-2 mb-2">
                                      <BarChart3 className="w-4 h-4 text-indigo-600" />
                                      <span className="text-sm font-medium text-gray-900">Pytest session</span>
                                      {detail.pytest_summary.pass_rate && (
                                        <span className="text-xs text-indigo-600">{detail.pytest_summary.pass_rate} pass rate</span>
                                      )}
                                      {detail.pytest_summary.environment && (
                                        <span className="text-xs text-gray-400">· {detail.pytest_summary.environment}</span>
                                      )}
                                      {detail.pytest_summary.elapsed_s != null && (
                                        <span className="text-xs text-gray-400">· {detail.pytest_summary.elapsed_s}s</span>
                                      )}
                                    </div>
                                    {detail.pytest_summary.batches && Object.keys(detail.pytest_summary.batches).length > 0 && (
                                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                        {Object.entries(detail.pytest_summary.batches as Record<string, any>)
                                          .sort(([, a]: any, [, b]: any) => b.failed - a.failed)
                                          .map(([batchName, stats]: [string, any]) => (
                                          <div key={batchName} className={`flex items-center justify-between px-3 py-2 rounded border text-xs ${
                                            stats.failed === 0 ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                                          }`}>
                                            <span className="font-mono text-gray-800 truncate mr-2">{batchName}</span>
                                            <span className={stats.failed === 0 ? 'text-green-700' : 'text-red-700'}>
                                              {stats.passed}/{stats.total} ({stats.pass_rate})
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}

                                {/* Failure list — primary content for fail runs */}
                                {Array.isArray(detail.failures) && detail.failures.length > 0 && (
                                  <div>
                                    <div className="flex items-center gap-2 mb-2">
                                      <XCircle className="w-4 h-4 text-red-600" />
                                      <span className="text-sm font-medium text-gray-900">
                                        Failures ({detail.failures.length})
                                      </span>
                                    </div>
                                    <div className="space-y-2">
                                      {detail.failures.map((f: any, i: number) => (
                                        <details key={i} className="bg-white border border-red-200 rounded">
                                          <summary className="px-3 py-2 cursor-pointer text-sm font-mono text-red-700 hover:bg-red-50">
                                            {f.nodeid || '(no nodeid)'}
                                          </summary>
                                          <div className="px-3 pb-3 pt-1 border-t border-red-100">
                                            {f.message && (
                                              <div className="text-xs text-gray-700 mb-2">
                                                <span className="font-medium">Message:</span> {f.message}
                                              </div>
                                            )}
                                            {f.traceback && (
                                              <pre className="text-[11px] font-mono bg-gray-900 text-gray-100 rounded p-2 overflow-x-auto whitespace-pre-wrap">
                                                {f.traceback}
                                              </pre>
                                            )}
                                          </div>
                                        </details>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Per-check list — only render if checks exist (some runs only carry failures) */}
                                {Array.isArray(detail.checks) && detail.checks.length > 0 && (
                                  <div>
                                    <div className="text-sm font-medium text-gray-900 mb-2">
                                      Checks ({detail.checks.length})
                                    </div>
                                    <div className="max-h-64 overflow-y-auto bg-white border border-gray-200 rounded divide-y divide-gray-100">
                                      {detail.checks.map((c: any, i: number) => {
                                        const cs = STATUS_STYLES[c.status] || STATUS_STYLES.error;
                                        return (
                                          <div key={i} className={`px-3 py-2 flex items-start gap-2 ${c.status === 'fail' ? 'bg-red-50' : ''}`}>
                                            <div className="mt-0.5">{cs.icon}</div>
                                            <div className="flex-1 min-w-0">
                                              <div className="text-xs font-mono text-gray-800 truncate">{c.name}</div>
                                              {c.detail && <div className="text-[11px] text-gray-500 mt-0.5 break-words">{c.detail}</div>}
                                            </div>
                                            <div className="text-[11px] text-gray-400">{c.duration_ms}ms</div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}

                                {/* Empty state — pass runs with no individual checks */}
                                {(!detail.failures || detail.failures.length === 0) &&
                                 (!detail.checks || detail.checks.length === 0) &&
                                 !detail.pytest_summary && (
                                  <div className="text-sm text-gray-500 italic">
                                    No per-test detail recorded for this run. (Custom regressions and external ingest sources without a JSON report fall into this case.)
                                  </div>
                                )}

                                {detail.branch && (
                                  <div className="text-xs text-gray-500">
                                    Branch: <span className="font-mono">{detail.branch}</span>
                                    {detail.commit_sha && (
                                      <> · Commit: <span className="font-mono">{detail.commit_sha}</span></>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="text-sm text-gray-500">Failed to load run detail.</div>
                            )}
                          </td>
                        </tr>
                      ),
                    ].filter(Boolean);
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
